import { filter, map, Observable } from 'rxjs';
import { TranslateModule } from '@ngx-translate/core';
import { createUserContextHeader, debugUserContextHeader, WO_CONTEXT_HEADER } from '@iss/libs/user-context';
import { Site, ServiceLocationsGQL, Maybe, Role } from '@iss/libs/graphql';
import { DefaultContext } from '@apollo/client/core/types';
import { FormControl, NgControl, ReactiveFormsModule } from '@angular/forms';
import {
  Component,
  Inject,
  inject,
  Input,
  OnChanges,
  Self,
  SimpleChanges,
} from '@angular/core';

import { ServiceLocation, ServiceLocations } from './service-location-input.types';

import { TypeaheadSelectComponent } from '@iss/core/typeahead-select';
import { Control, NOOP_VALUE_ACCESSOR } from '@iss/core/forms';

@Component({
  selector: 'wo-service-location-input',
  templateUrl: './service-location-input.component.html',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    TranslateModule,
    TypeaheadSelectComponent,
  ],
})
export class ServiceLocationInputComponent implements OnChanges {
  private readonly query = inject(ServiceLocationsGQL);

  public loading$!: Observable<boolean>;
  public items$!: Observable<ServiceLocations>;

  public readonly stringify = (serviceLocations: ServiceLocations | null): (id: ServiceLocation['id'] | 'ALL') => string =>
    id => serviceLocations?.find(serviceLocation => serviceLocation.id === id)?.name || '';

  public readonly matcher = (serviceLocation: ServiceLocation): string => serviceLocation.name || '';

  public readonly content = (id: ServiceLocation['id'] | 'ALL', serviceLocations: ServiceLocations | null): string | undefined =>
    id === 'ALL'
      ? 'All'
      : serviceLocations?.find(serviceLocation => serviceLocation.id === id)?.name || undefined;

  public readonly identity = (serviceLocation: ServiceLocation): ServiceLocation['id'] => serviceLocation.id;

  public constructor(
    @Self() @Inject(NgControl) public readonly control: Control<FormControl>,
  ) {
    this.control.valueAccessor = NOOP_VALUE_ACCESSOR;
  }

  @Input() public withAll: Maybe<boolean>;
  @Input() public loading: Maybe<boolean>;
  @Input() public readonly: Maybe<boolean>;
  @Input() public forceTypeahead: Maybe<boolean>;
  @Input() public placeholder: Maybe<string>;
  @Input() public siteId: Maybe<Site['id']>;
  @Input() public role: Maybe<Role>;
  @Input() public clearable = true;
  @Input() public multi: Maybe<boolean>;

  public ngOnChanges(changes: SimpleChanges): void {
    if (!this.items$ || 'siteId' in changes || 'role' in changes) {
      this.initQuery();
    }
  }

  private initQuery(): void {
    const query$ = this.query.watch({
      serviceLocationsFiltersInput: {
        offset: 0,
        limit: 999,
        siteId: this.siteId,
      },
    }, {
      fetchPolicy: this.role ? 'no-cache' : undefined,
      context: this.context,
    }).valueChanges;

    this.loading$ = query$.pipe(map(({ loading }) => loading));
    this.items$ = query$.pipe(
      filter(({ data }) => Boolean(data)),
      map(({ data }) => data.serviceLocations.items),
    );
  }

  private get context(): DefaultContext | undefined {
    if (!this.role) return;

    const userContextHeader = createUserContextHeader({ role: this.role, site: this.siteId });
    debugUserContextHeader('service-location-input', userContextHeader);

    return {
      headers: {
        [WO_CONTEXT_HEADER]: userContextHeader,
      },
    };
  }
}
